import React from 'react';
import ReactDOM from 'react-dom/client';
import { createBrowserRouter, RouterProvider, redirect } from "react-router-dom";

import ErrorPage from './error-page';

import Root from "./routes/root";
import Home from "./routes/home";
import Post from "./routes/post";
import AboutUs from "./routes/about-us";
import Events from "./routes/events";
import Event from "./routes/event";
import Contact from "./routes/contact";
import JoinUs from "./routes/join-us";
import RequestFunding from "./routes/request-funding";
import RequestFundingStartupScaleup from "./routes/request-funding-startup-scaleup";
import RequestFundingLargeProject from "./routes/request-funding-large-project";
import ExecutiveEducationApply from "./routes/executive-education-apply";

import AdminLayout from "./routes/admin/layout";
import AdminLogin from "./routes/admin/login";
import AdminDashboard from "./routes/admin/dashboard";
import AdminEvents from "./routes/admin/events/index";
import AdminEventForm from "./routes/admin/events/form";
import AdminPosts from "./routes/admin/posts/index";
import AdminPostForm from "./routes/admin/posts/form";
import AdminTeamMembers from "./routes/admin/team-members/index";
import AdminTeamMemberForm from "./routes/admin/team-members/form";
import AdminMembers from "./routes/admin/members/index";
import AdminMemberForm from "./routes/admin/members/form";
import AdminPartners from "./routes/admin/partners/index";
import AdminPartnerForm from "./routes/admin/partners/form";

import { listPosts, listPartners, getPost, listEvents, getEvent, listTeamMembers, listMembers, getTeamMember, getMember, getPartner } from './api/cms';

import './index.css';

const requireAuth = () => {
  if (!localStorage.getItem('bah_cms_key')) {
    return redirect('/admin/login');
  }
  return null;
};

const router = createBrowserRouter([
  {
    path: "/",
    element: <Root />,
    errorElement: <ErrorPage />,
    children: [
      {
        index: true,
        element: <Home />,
        loader: async () => {
          const [posts, partners] = await Promise.all([listPosts(), listPartners()]);
          const sortedPosts = [...posts].sort((a, b) => new Date(b.date) - new Date(a.date));
          const sortedPartners = [...partners].sort((a, b) => {
            if (a.order == null) return 1;
            if (b.order == null) return -1;
            return a.order - b.order;
          });
          return { posts: sortedPosts, partners: sortedPartners };
        },
      },
      {
        path: "posts/:id",
        element: <Post />,
        loader: async ({ params }) => {
          const post = await getPost(params.id);
          return post;
        },
      },
      {
        path: "posts/:id/:slug",
        element: <Post />,
        loader: async ({ params }) => {
          const post = await getPost(params.id);
          return post;
        },
      },
      {
        path: "about-us",
        element: <AboutUs />,
        loader: async () => {
          const [partners, members, teamMembers] = await Promise.all([
            listPartners(),
            listMembers(),
            listTeamMembers(),
          ]);
          const byOrder = (a, b) => {
            if (a.order == null) return 1;
            if (b.order == null) return -1;
            return a.order - b.order;
          };
          return {
            partners: [...partners].sort(byOrder),
            members: [...members].sort(byOrder),
            teamMembers: [...teamMembers].sort(byOrder),
          };
        },
      },
      {
        path: "events",
        element: <Events />,
        loader: async () => {
          const events = await listEvents();
          return { events };
        },
      },
      {
        path: "events/:id",
        element: <Event />,
        loader: async ({ params }) => {
          const event = await getEvent(params.id);
          return event;
        },
      },
      {
        path: "events/:id/:slug",
        element: <Event />,
        loader: async ({ params }) => {
          const event = await getEvent(params.id);
          return event;
        },
      },
      {
        path: "contact",
        element: <Contact />,
      },
      {
        path: "join-us",
        element: <JoinUs />,
      },
      {
        path: "request-funding",
        element: <RequestFunding />,
      },
      {
        path: "request-funding-startup-scaleup",
        element: <RequestFundingStartupScaleup />,
      },
      {
        path: "request-funding-large-project",
        element: <RequestFundingLargeProject />,
      },
      {
        path: "executive-education/apply",
        element: <ExecutiveEducationApply />,
      },
    ],
  },
  {
    path: "/admin",
    element: <AdminLayout />,
    children: [
      {
        index: true,
        loader: () => redirect('/admin/dashboard'),
        element: null,
      },
      {
        path: "login",
        element: <AdminLogin />,
      },
      {
        path: "dashboard",
        loader: requireAuth,
        element: <AdminDashboard />,
      },
      {
        path: "events",
        loader: requireAuth,
        element: <AdminEvents />,
      },
      {
        path: "events/new",
        loader: requireAuth,
        element: <AdminEventForm />,
      },
      {
        path: "events/:id/edit",
        loader: async ({ params }) => {
          const auth = requireAuth();
          if (auth) return auth;
          return getEvent(params.id);
        },
        element: <AdminEventForm />,
      },
      {
        path: "posts",
        loader: requireAuth,
        element: <AdminPosts />,
      },
      {
        path: "posts/new",
        loader: requireAuth,
        element: <AdminPostForm />,
      },
      {
        path: "posts/:id/edit",
        loader: async ({ params }) => {
          const auth = requireAuth();
          if (auth) return auth;
          return getPost(params.id);
        },
        element: <AdminPostForm />,
      },
      {
        path: "team-members",
        loader: requireAuth,
        element: <AdminTeamMembers />,
      },
      {
        path: "team-members/new",
        loader: requireAuth,
        element: <AdminTeamMemberForm />,
      },
      {
        path: "team-members/:id/edit",
        loader: async ({ params }) => {
          const auth = requireAuth();
          if (auth) return auth;
          return getTeamMember(params.id);
        },
        element: <AdminTeamMemberForm />,
      },
      {
        path: "members",
        loader: requireAuth,
        element: <AdminMembers />,
      },
      {
        path: "members/new",
        loader: requireAuth,
        element: <AdminMemberForm />,
      },
      {
        path: "members/:id/edit",
        loader: async ({ params }) => {
          const auth = requireAuth();
          if (auth) return auth;
          return getMember(params.id);
        },
        element: <AdminMemberForm />,
      },
      {
        path: "partners",
        loader: requireAuth,
        element: <AdminPartners />,
      },
      {
        path: "partners/new",
        loader: requireAuth,
        element: <AdminPartnerForm />,
      },
      {
        path: "partners/:id/edit",
        loader: async ({ params }) => {
          const auth = requireAuth();
          if (auth) return auth;
          return getPartner(params.id);
        },
        element: <AdminPartnerForm />,
      },
    ],
  },
]);

ReactDOM.createRoot(document.getElementById("root")).render(
  <React.StrictMode>
    <RouterProvider router={router} />
  </React.StrictMode>
);
